import { userModel } from 'entities/user';
import { ContractsNames, ContractsService, fromDecimals } from 'shared';
import { error, request, success } from 'shared/api';
import { all, call, put, select, takeLatest } from 'typed-redux-saga';

import { userActions } from '../reducer';

import { getTokenBalanceSaga } from './getTokenBalance';

const TOKEN_DECIMALS = 18;
const RATES_COUNT = 4;

export function* getVestingSwapInfoSaga({
  type,
  payload: { web3Provider },
}: ReturnType<typeof userActions.getVestingSwapInfo>) {
  try {
    yield put(request(type));

    const { address: userWalletAddress, chainType, network } = yield select(userModel.selectors.selectUserWeb3Info);
    const { address: starsV1Address } = ContractsService.getContractData(ContractsNames.StarsV1, network, chainType);
    const { address: starsV2Address } = ContractsService.getContractData(ContractsNames.StarsV2, network, chainType);
    const { address: vestingSwapAddress } = ContractsService.getContractData(
      ContractsNames.StarsVestingSwap,
      network,
      chainType,
    );

    yield all([
      call(getTokenBalanceSaga, {
        type: userActions.getTokenBalance.type,
        payload: {
          tokenAddress: starsV1Address,
          isNative: false,
          web3Provider,
        },
      }),
      call(getTokenBalanceSaga, {
        type: userActions.getTokenBalance.type,
        payload: {
          tokenAddress: starsV2Address,
          isNative: false,
          web3Provider,
        },
      }),
    ]);

    const contractsService = new ContractsService(web3Provider);
    const vestingSwapContract = contractsService.getStarsVestingSwapContract(vestingSwapAddress);

    const indexesArray = new Array(RATES_COUNT).fill(0).map((_, i) => i);

    const lockTimes = yield* all(indexesArray.map((_, i) => call(vestingSwapContract.methods.LOCK_TIME(i).call)));
    const swapRates = yield* all(indexesArray.map((_, i) => call(vestingSwapContract.methods.SWAP_RATE(i).call)));
    const allTimeRewardsInfo = yield* all(
      indexesArray.map((_, i) => call(vestingSwapContract.methods.allTimeUserRewardInfo(userWalletAddress, i).call)),
    );

    const [startTime, ENTRANCE_DURATION, SWAP_RATE_DENOMINATOR, availableToClaim] = yield* all([
      call(vestingSwapContract.methods.startTime().call),
      call(vestingSwapContract.methods.ENTRANCE_DURATION().call),
      call(vestingSwapContract.methods.DENOMINATOR().call),
      call(vestingSwapContract.methods.availableToClaim(userWalletAddress).call),
    ]);

    const finish = Number(startTime) + Number(ENTRANCE_DURATION);
    const vestingRates: [number, number][] = indexesArray.map((i) => [
      Number(lockTimes[i]), // seconds
      Number(swapRates[i]),
    ]);
    const vestingRewards: [number, string][] = indexesArray.map((i) => [
      Math.floor(Number(lockTimes[i]) + finish),
      fromDecimals(allTimeRewardsInfo[i], TOKEN_DECIMALS),
    ]);
    const availableToClaimSum = Number(fromDecimals(availableToClaim[0], TOKEN_DECIMALS));

    yield put(
      userActions.updateVestingSwapInfo({
        finish,
        vestingRates,
        vestingRewards,
        availableToClaimSum,
        startTime: Number(startTime),
        swapRateDenominator: Number(SWAP_RATE_DENOMINATOR),
      }),
    );

    yield put(success(type));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    yield put(error(type));
  }
}

export default function* listener() {
  yield takeLatest(userActions.getVestingSwapInfo.type, getVestingSwapInfoSaga);
}
